import React from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';

const DetailContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #E5E7EB;
`;

const SubSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SubSectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.75rem 0;
`;

const ContentBox = styled.div`
  background: #f3f5f7;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 1.25rem;
  
  p {
    margin: 0 0 0.5rem 0;
    color: #374151;
    line-height: 1.6;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul, ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    color: #374151;
  }
  
  li {
    margin: 0.25rem 0;
    line-height: 1.6;
  }
  
  h3 {
    font-size: 0.95rem;
    font-weight: 600;
    color: #111827;
    margin: 1rem 0 0.5rem 0;
    
    &:first-child {
      margin-top: 0;
    }
  }
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const MetricCard = styled.div`
  background: linear-gradient(135deg, #667EEA 0%, #764BA2 100%);
  border-radius: 12px;
  padding: 1.5rem;
  color: white;
`;

const MetricLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.9;
  margin-bottom: 0.5rem;
`;

const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
`;

const ConfidenceBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.percent >= 80 ? '#10B981' : props.percent >= 60 ? '#F59E0B' : '#EF4444'};
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const KeySignalsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const KeySignalItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f3f5f7;
  border-left: 3px solid #3B82F6;
  border-radius: 4px;
  
  &::before {
    content: '✓';
    color: #10B981;
    font-weight: 700;
    flex-shrink: 0;
  }
`;

const StandardAIDetail = ({ card }) => {
  if (!card) {
    return (
      <DetailContainer>
        <p>No card data available.</p>
      </DetailContainer>
    );
  }

  return (
    <DetailContainer>
      {/* Header Section with Metrics */}
      <Section>
        <MetricGrid>
          {card.metric && (
            <MetricCard>
              <MetricLabel>{card.description || 'Metric'}</MetricLabel>
              <MetricValue>{card.metric}</MetricValue>
            </MetricCard>
          )}
          {card.confidencePercent && (
            <MetricCard style={{ background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)' }}>
              <MetricLabel>Confidence</MetricLabel>
              <MetricValue>{card.confidencePercent}%</MetricValue>
            </MetricCard>
          )}
        </MetricGrid>
      </Section>

      {/* Subtitle */}
      {card.subtitle && (
        <Section>
          <SectionTitle>Status</SectionTitle>
          <ContentBox>
            <p><strong>{card.subtitle}</strong></p>
          </ContentBox>
        </Section>
      )}

      {/* Key Signals */}
      {card.keySignalsText && (
        <Section>
          <SectionTitle>Key Signals</SectionTitle>
          <KeySignalsList>
            {card.keySignalsText.split('\n').filter(s => s.trim()).map((signal, index) => (
              <KeySignalItem key={index}>
                {signal.replace(/^-\s*/, '').replace(/^✅\s*/, '')}
              </KeySignalItem>
            ))}
          </KeySignalsList>
        </Section>
      )}

      {/* More Information */}
      {card.moreInformation && (
        <Section>
          <SectionTitle>Detailed Information</SectionTitle>
          
          {card.moreInformation.sourceInput && (
            <SubSection>
              <SubSectionTitle>Source Input</SubSectionTitle>
              <ContentBox>
                <ReactMarkdown>{card.moreInformation.sourceInput}</ReactMarkdown>
              </ContentBox>
            </SubSection>
          )}

          {card.moreInformation.logic && (
            <SubSection>
              <SubSectionTitle>Validation Logic</SubSectionTitle>
              <ContentBox>
                <ReactMarkdown>{card.moreInformation.logic}</ReactMarkdown>
              </ContentBox>
            </SubSection>
          )}

          {card.moreInformation.output && (
            <SubSection>
              <SubSectionTitle>Output Determination</SubSectionTitle>
              <ContentBox>
                <ReactMarkdown>{card.moreInformation.output}</ReactMarkdown>
              </ContentBox>
            </SubSection>
          )}
        </Section>
      )}

      {/* Confidence Badge */}
      {card.confidencePercent && (
        <Section>
          <ConfidenceBadge percent={card.confidencePercent}>
            <span>🎯</span>
            Confidence: {card.confidencePercent}%
          </ConfidenceBadge>
        </Section>
      )}
    </DetailContainer>
  );
};

export default StandardAIDetail;
